/**
 * Utilitário para upload de arquivos grandes em chunks
 * Resolve problemas de timeout e limitações de tamanho
 */
import { logDebug } from "./logger";
import { CAMPINA_API, PATOS_API } from "../api/InstanceAPI";

// Configurações
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB por chunk
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 segundo

/**
 * Divide um arquivo em chunks menores
 * @param {File} file - Arquivo a ser dividido
 * @param {number} chunkSize - Tamanho de cada chunk em bytes
 * @returns {Array} Array de chunks
 */
export const createFileChunks = (file, chunkSize = CHUNK_SIZE) => {
  const chunks = [];
  let start = 0;
  
  // Gerar fileId uma única vez para todos os chunks
  const fileId = `${file.name}_${Date.now()}`;
  const totalChunks = Math.ceil(file.size / chunkSize);
  
  while (start < file.size) {
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    chunks.push({
      chunk,
      start,
      end,
      index: chunks.length,
      totalChunks,
      fileId,
      fileName: file.name,
      fileSize: file.size
    });
    start = end;
  }
  
  return chunks;
};

/**
 * Constrói a URL completa baseada na unidade do usuário
 * Usa as URLs configuradas em InstanceAPI.js para garantir consistência
 * @returns {string} URL completa da API
 */
const getBaseUrl = () => {
  const storageData = sessionStorage.getItem("loggedUser");
  let user = "";
  
  if (storageData) {
    user = JSON.parse(storageData);
  }
  
  // Usa as URLs exportadas de InstanceAPI.js que já consideram o ambiente
  return user.unit === "CAMPINA GRANDE" ? CAMPINA_API : PATOS_API;
};

/**
 * Obtém o token de autenticação do usuário logado
 * @returns {string|null} Token JWT ou null
 */
const getAuthToken = () => {
  try {
    const storageData = sessionStorage.getItem("loggedUser");
    if (storageData) {
      const user = JSON.parse(storageData);
      return user.token || null;
    }
  } catch (error) {
    // Erro silencioso ao obter token
  }
  return null;
};

/**
 * Faz upload de um chunk individual
 * @param {Object} chunkData - Dados do chunk
 * @param {string} uploadUrl - URL do endpoint de upload (relativa)
 * @param {Object} additionalData - Dados adicionais (AlunoID, Nome, etc.)
 * @returns {Promise} Promise do upload
 */
export const uploadChunk = async (chunkData, uploadUrl, additionalData = {}) => {
  const formData = new FormData();
  
  // Criar um File a partir do Blob para que o multer reconheça
  const chunkFile = new File([chunkData.chunk], `chunk_${chunkData.index}`, {
    type: 'application/octet-stream'
  });
  
  // Adicionar o chunk como File (não Blob)
  formData.append('chunk', chunkFile);
  formData.append('chunkIndex', String(chunkData.index));
  formData.append('totalChunks', String(chunkData.totalChunks));
  formData.append('fileId', String(chunkData.fileId ?? ''));
  formData.append('fileName', String(chunkData.fileName ?? ''));
  formData.append('fileSize', String(chunkData.fileSize ?? 0));
  formData.append('start', String(chunkData.start ?? 0));
  formData.append('end', String(chunkData.end ?? 0));
  
  // Dados obrigatórios para o backend (AlunoID e Nome)
  const alunoId = additionalData?.AlunoID != null && additionalData?.AlunoID !== '' ? String(additionalData.AlunoID) : '';
  const nome = additionalData?.Nome != null && additionalData?.Nome !== '' ? String(additionalData.Nome) : '';
  formData.append('AlunoID', alunoId);
  formData.append('Nome', nome);
  
  // Outros dados adicionais (evitar sobrescrever os já adicionados)
  Object.keys(additionalData || {}).forEach(key => {
    if (key === 'AlunoID' || key === 'Nome') return;
    const val = additionalData[key];
    if (val !== undefined && val !== null) formData.append(key, typeof val === 'string' ? val : String(val));
  });
  
  // Incluir todos os campos obrigatórios na query para o backend receber (multer pode não popular req.body em multipart)
  const baseUrl = getBaseUrl();
  const params = new URLSearchParams();
  params.set('chunkIndex', String(chunkData.index));
  params.set('totalChunks', String(chunkData.totalChunks));
  params.set('fileId', String(chunkData.fileId ?? ''));
  params.set('fileName', String(chunkData.fileName ?? ''));
  if (alunoId) params.set('AlunoID', alunoId);
  if (nome) params.set('Nome', nome);
  const fullUrl = `${baseUrl}${uploadUrl}?${params.toString()}`;
  
  logDebug(`Enviando chunk ${chunkData.index} para: ${fullUrl}`);
  logDebug(`Chunk size: ${chunkData.chunk.size} bytes`);
  logDebug(`Chunk type: ${chunkData.chunk.constructor.name}`);
  logDebug(`ChunkFile type: ${chunkFile.constructor.name}`);
  logDebug(`ChunkFile size: ${chunkFile.size} bytes`);
  logDebug(
    `FormData entries:`,
    Array.from(formData.entries()).map(([key, value]) => [
      key,
      value.constructor.name,
      value.size || value,
    ])
  );
  
  // Obter token de autenticação
  const token = getAuthToken();
  const headers = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(fullUrl, {
    method: 'POST',
    headers,
    body: formData,
    // Não definir Content-Type, deixar o browser definir automaticamente
  });
  
  if (!response.ok) {
    // Tratar erro 401 (não autorizado) - token inválido/expirado
    if (response.status === 401) {
      // Limpar sessão e redirecionar para login
      sessionStorage.removeItem("loggedUser");
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    
    const errorText = await response.text();
    console.error(`Erro no chunk ${chunkData.index}:`, errorText);
    throw new Error(`Erro no upload do chunk ${chunkData.index}: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Faz upload de um arquivo completo usando chunks
 * @param {File} file - Arquivo a ser enviado
 * @param {string} uploadUrl - URL do endpoint de upload
 * @param {Object} additionalData - Dados adicionais
 * @param {Function} onProgress - Callback de progresso (opcional)
 * @returns {Promise} Promise do upload completo
 */
export const uploadFileInChunks = async (file, uploadUrl, additionalData = {}, onProgress = null) => {
  // Sempre usar formato de chunk (um ou vários) para o endpoint /documents/upload-chunks
  logDebug(
    `Iniciando upload em chunks para arquivo: ${file.name} (${file.size} bytes)`
  );
  
  const chunks = createFileChunks(file);
  const results = [];
  
  // Função auxiliar para upload com retry
  const uploadChunkWithRetry = async (chunk, chunkIndex, totalChunks) => {
    let retries = 0;
    
    while (retries < MAX_RETRIES) {
      try {
        logDebug(
          `Enviando chunk ${chunkIndex + 1}/${totalChunks} (${chunk.chunk.size} bytes)`
        );
        
        const result = await uploadChunk(chunk, uploadUrl, additionalData);
        
        // Atualizar progresso
        if (onProgress) {
          const progress = ((chunkIndex + 1) / totalChunks) * 100;
          onProgress(progress, chunkIndex + 1, totalChunks);
        }
        
        return result;
        
      } catch (error) {
        retries++;
        console.error(`Erro no chunk ${chunkIndex + 1}, tentativa ${retries}/${MAX_RETRIES}:`, error);
        
        if (retries < MAX_RETRIES) {
          const delay = RETRY_DELAY * retries;
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          throw new Error(`Falha ao enviar chunk ${chunkIndex + 1} após ${MAX_RETRIES} tentativas: ${error.message}`);
        }
      }
    }
  };

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    const result = await uploadChunkWithRetry(chunk, i, chunks.length);
    results.push(result);
  }
  
  logDebug("Upload em chunks concluído com sucesso");
  return {
    success: true,
    chunks: results,
    totalChunks: chunks.length,
    fileSize: file.size
  };
};

/**
 * Upload direto para arquivos pequenos
 * @param {File} file - Arquivo a ser enviado
 * @param {string} uploadUrl - URL do endpoint (relativa)
 * @param {Object} additionalData - Dados adicionais
 * @returns {Promise} Promise do upload
 */
export const uploadFileDirect = async (file, uploadUrl, additionalData = {}) => {
  const formData = new FormData();
  formData.append('documents', file);
  
  // Adicionar dados adicionais
  Object.keys(additionalData).forEach(key => {
    formData.append(key, additionalData[key]);
  });
  
  // Construir URL completa
  const baseUrl = getBaseUrl();
  const fullUrl = `${baseUrl}${uploadUrl}`;
  
  logDebug(`Enviando arquivo direto para: ${fullUrl}`);
  
  // Obter token de autenticação
  const token = getAuthToken();
  const headers = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(fullUrl, {
    method: 'POST',
    headers,
    body: formData,
  });
  
  if (!response.ok) {
    // Tratar erro 401 (não autorizado) - token inválido/expirado
    if (response.status === 401) {
      // Limpar sessão e redirecionar para login
      sessionStorage.removeItem("loggedUser");
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    
    const errorText = await response.text();
    console.error('Erro no upload direto:', errorText);
    throw new Error(`Erro no upload direto: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Upload de múltiplos arquivos com controle de concorrência
 * @param {Array} files - Array de arquivos
 * @param {string} uploadUrl - URL do endpoint
 * @param {Object} additionalData - Dados adicionais
 * @param {Function} onProgress - Callback de progresso
 * @param {number} maxConcurrent - Máximo de uploads simultâneos
 * @returns {Promise} Promise dos uploads
 */
export const uploadMultipleFiles = async (files, uploadUrl, additionalData = {}, onProgress = null, maxConcurrent = 3) => {
  const results = [];
  const errors = [];
  
  // Processar arquivos em lotes para controlar concorrência
  for (let i = 0; i < files.length; i += maxConcurrent) {
    const batch = files.slice(i, i + maxConcurrent);
    
    const batchPromises = batch.map(async (file, batchIndex) => {
      const fileIndex = i + batchIndex;
      try {
        const result = await uploadFileInChunks(file, uploadUrl, additionalData, (progress) => {
          if (onProgress) {
            // Calcular progresso global
            const globalProgress = ((fileIndex + progress / 100) / files.length) * 100;
            onProgress(globalProgress, fileIndex + 1, files.length, file.name);
          }
        });
        
        return { success: true, file: file.name, result };
      } catch (error) {
        console.error(`Erro no upload do arquivo ${file.name}:`, error);
        return { success: false, file: file.name, error: error.message };
      }
    });
    
    const batchResults = await Promise.allSettled(batchPromises);
    
    batchResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          results.push(result.value);
        } else {
          errors.push(result.value);
        }
      } else {
        errors.push({
          success: false,
          file: batch[index].name,
          error: result.reason?.message || 'Erro desconhecido'
        });
      }
    });
  }
  
  return {
    results,
    errors,
    totalFiles: files.length,
    successfulUploads: results.length,
    failedUploads: errors.length
  };
};

/**
 * Utilitário para formatar tamanho de arquivo
 * @param {number} bytes - Tamanho em bytes
 * @returns {string} Tamanho formatado
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Verifica se um arquivo é muito grande para upload
 * @param {File} file - Arquivo a ser verificado
 * @param {number} maxSize - Tamanho máximo em bytes (padrão: 100MB)
 * @returns {Object} Resultado da verificação
 */
export const validateFileSize = (file, maxSize = 100 * 1024 * 1024) => {
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `Arquivo muito grande. Tamanho: ${formatFileSize(file.size)}, Máximo permitido: ${formatFileSize(maxSize)}`
    };
  }
  
  return { valid: true };
};


